import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Activity, ArrowRight } from "lucide-react";
import { api, type OverviewResult } from "@/lib/api";
import { ErrorBox, PageHeader, Spinner, StatTile } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) n = 0;
  const units = ["B", "KiB", "MiB", "GiB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return i === 0 ? `${Math.round(v)} ${units[i]}` : `${v.toFixed(1)} ${units[i]}`;
}

function statusVariant(
  s: string,
): "success" | "warning" | "danger" | "secondary" {
  switch (s) {
    case "healthy":
      return "success";
    case "degraded":
      return "warning";
    case "down":
      return "danger";
    default:
      return "secondary";
  }
}

function drillHref(projectId: string, drill: string): string {
  switch (drill) {
    case "gateway":
      return `/dashboard/projects/${projectId}/observability/gateway`;
    case "database":
      return `/dashboard/projects/${projectId}/observability/database`;
    case "auth":
      return `/dashboard/projects/${projectId}/observability/auth`;
    case "query-performance":
      return `/dashboard/projects/${projectId}/observability/gateway`;
    default:
      return `/dashboard/projects/${projectId}/observability`;
  }
}

export function ObservabilityOverviewPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [data, setData] = useState<OverviewResult | null>(null);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        const { data: ov } = await api.observabilityOverview(id);
        setData(ov);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <Spinner label="Loading observability…" />;

  return (
    <div>
      <PageHeader
        eyebrow="Observability"
        title="Health overview"
        description="Project health snapshot: traffic, errors, slow queries, schema size, and service matrix."
      />
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}
      {!data ? (
        <p className="text-sm text-text-muted">No overview data.</p>
      ) : (
        <div className="space-y-6">
          <div className="statusline !border rounded-md !px-3 !py-2">
            <span className="status-dot" />
            <span className="text-text-disabled">freshness</span>
            <span className="text-text-secondary">{data.data_freshness}</span>
            <span className="statusline-sep">·</span>
            <span className="truncate text-text-muted">{data.attribution_note}</span>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <StatTile
              label="Requests"
              value={data.request_volume.toLocaleString()}
            />
            <StatTile
              label="Error rate"
              value={`${data.api_error_rate.toFixed(2)}%`}
            />
            <StatTile
              label="API p95"
              value={`${data.api_p95_ms.toFixed(0)} ms`}
            />
            <StatTile
              label="Slow queries"
              value={data.slow_queries.toLocaleString()}
            />
            <StatTile
              label="Schema size"
              value={formatBytes(data.schema_size_bytes)}
            />
            <StatTile
              label="Connections"
              value={data.peak_project_connections}
              hint="project estimate"
            />
          </div>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-secondary" />
                  Service health
                </CardTitle>
                <Badge variant="secondary">
                  {data.services.length} services
                </Badge>
              </div>
              <CardDescription>
                Drill into a service for gateway, database, or auth detail.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {data.services.map((svc) => (
                  <Link
                    key={svc.service}
                    to={drillHref(id, svc.drill_down)}
                    className={cn(
                      "group rounded-lg border border-border-standard bg-surface-container-low/40 p-4 transition-[border-color,background-color,box-shadow,opacity] duration-standard ease-standard hover:border-secondary/40 hover:bg-surface-container-lowest hover:shadow-none",
                      !svc.capability_enabled && "opacity-60",
                    )}
                    title={svc.rule}
                  >
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="font-sans text-sm font-semibold text-text-primary group-hover:text-secondary">
                        {svc.service}
                      </span>
                      <Badge
                        variant={statusVariant(svc.status)}
                        className="uppercase"
                      >
                        {svc.status}
                      </Badge>
                    </div>
                    <dl className="grid grid-cols-2 gap-1 text-xs text-text-muted">
                      <div>
                        Events{" "}
                        <span className="tabular-nums text-text-primary">
                          {svc.requests.toLocaleString()}
                        </span>
                      </div>
                      <div>
                        p95{" "}
                        <span className="tabular-nums text-text-primary">
                          {svc.p95_ms.toFixed(0)}ms
                        </span>
                      </div>
                      <div
                        className="col-span-2 truncate font-mono text-[10px]"
                        title={svc.rule}
                      >
                        {svc.rule}
                      </div>
                    </dl>
                    <div className="mt-2 flex items-center gap-1 text-[11px] font-medium text-secondary opacity-0 transition-opacity duration-fast ease-standard group-hover:opacity-100">
                      Drill down
                      <ArrowRight className="h-3 w-3" />
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
